import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { UTC_INPUT_HINT, utcInputToIso } from '../../utils/utcDateTime';
import RichTextEditor from '../shop-hours/RichTextEditor';
import ImageUploadField from '../shared-components/ImageUploadField';
import '../shop-hours/shopHourModal.css';
import './announcementModal.css';

const AnnouncementForm = ({ formData, onChange, dateError, nameError, bodyError, token }) => {
  const handleInputChange = field => event => onChange(field, event.target.value);
  const handleCheckboxChange = field => event => onChange(field, event.target.checked);

  const sanitizedPreview = DOMPurify.sanitize(formData.description || '');

  const parseUtcInput = value => {
    const iso = utcInputToIso(value);
    if (!iso) return null;
    const parsed = DateTime.fromISO(iso, { zone: 'utc' });
    return parsed.isValid ? parsed : null;
  };

  const startDate = parseUtcInput(formData.startTime);
  const endDate = parseUtcInput(formData.endTime);

  const now = DateTime.utc();
  const statusDetails = (() => {
    if (!formData.active) {
      return { label: 'Hidden', variant: 'draft' };
    }
    if (startDate && endDate) {
      if (endDate < now) return { label: 'Ended', variant: 'ended' };
      if (startDate > now) return { label: 'Upcoming', variant: 'upcoming' };
      return { label: 'Live now', variant: 'live' };
    }
    return { label: 'Draft', variant: 'draft' };
  })();

  const previewTitle = formData.name?.trim() || 'Untitled announcement';

  return (
    <Form className="shop-hour-form announcement-form">
      <Row className="g-4">
        <Col lg={5}>
          <div className="section-card mb-4">
            <div className="section-header">
              <h6 className="section-title">Announcement Details</h6>
              <p className="section-subtitle">
                Shown as a popup on the game front page while the window is open.
              </p>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter announcement name"
                value={formData.name}
                onChange={handleInputChange('name')}
                isInvalid={Boolean(nameError)}
              />
              {nameError && <div className="text-danger small mt-1">{nameError}</div>}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Check
                type="switch"
                id="announcement-active-toggle"
                label="Show this announcement"
                checked={Boolean(formData.active)}
                onChange={handleCheckboxChange('active')}
              />
            </Form.Group>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h6 className="section-title">Schedule</h6>
              <p className="section-subtitle">
                The window only controls visibility — players never see these dates.
              </p>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Start Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.startTime}
                onChange={handleInputChange('startTime')}
              />
              <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>End Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.endTime}
                onChange={handleInputChange('endTime')}
              />
              <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
              {dateError ? (
                <div className="text-danger small mt-1">{dateError}</div>
              ) : (
                <Form.Text muted>The popup and its megaphone icon disappear after this.</Form.Text>
              )}
            </Form.Group>
          </div>
        </Col>

        <Col lg={7}>
          <div className="section-card h-100">
            <div className="section-header">
              <h6 className="section-title">Image</h6>
              <p className="section-subtitle">
                Optional banner shown at the top of the popup, cropped to a wide strip.
              </p>
            </div>
            <Form.Group>
              <ImageUploadField
                value={formData.imageUrl}
                onChange={url => onChange('imageUrl', url)}
                token={token}
                id="announcement-image"
              />
            </Form.Group>
          </div>
        </Col>
      </Row>

      <div className="section-card mt-4">
        <div className="section-header">
          <h6 className="section-title mb-0">Announcement Content</h6>
          <p className="section-subtitle mb-0">Craft a clear, friendly message for your players.</p>
        </div>

        <div className="editor-preview-grid rich-text-wrapper mt-3">
          <div className="rich-text-editor-pane">
            <RichTextEditor
              value={formData.description}
              onChange={value => onChange('description', value)}
              placeholder="Enter announcement content..."
            />
            {bodyError && <div className="text-danger small mt-1">{bodyError}</div>}
          </div>
          <div className="preview-pane">
            <div className="preview-header">
              <div>
                <div className="preview-title">Live Preview</div>
                <p className="preview-helper-text mb-0">
                  Matches the player-facing announcement popup.
                </p>
              </div>
              <span className={`announcement-status announcement-status--${statusDetails.variant}`}>
                {statusDetails.label}
              </span>
            </div>

            <div className="site-announcement-popup-preview">
              <div className="site-announcement-popup-eyebrow">DCF ANNOUNCEMENT</div>
              {formData.imageUrl && (
                <img className="site-announcement-popup-banner" src={formData.imageUrl} alt="" />
              )}
              <div className="site-announcement-popup-inner">
                <h5 className="site-announcement-popup-title">{previewTitle}</h5>
                {sanitizedPreview ? (
                  <div
                    className="site-announcement-popup-body"
                    dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
                  />
                ) : (
                  <p className="site-announcement-popup-placeholder mb-0">
                    Use the editor to craft your message. Headings, lists, and links render here
                    exactly as they do for players.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default AnnouncementForm;
